import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  catchError,
  map,
  Observable,
  Subject,
  takeUntil,
  throwError,
} from 'rxjs';
import { chatbotApiUrl } from '../tokens/chatbot-api-url.token';

export interface ChatApiResponse {
  message: string;
  conversationId: string;
  messageId: string;
  role: string;
  citations?: Citations[];
}

export interface Citations {
  title: string;
  docNumber: string;
  content: string;
  url: string;
}

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
    Expires: 'Sat, 01 Jan 2000 00:00:00 GMT',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class ChatApiService {
  private chatbotApiUrl = inject(chatbotApiUrl);
  private cancelRequest$ = new Subject<void>();

  constructor(private http: HttpClient) {}

  public getAvailability(): Observable<boolean> {
    const url = `${this.chatbotApiUrl}/checkavailability`;
    return this.http.get(url, { ...httpOptions, responseType: 'text' }).pipe(
      map((response: string) => {
        return response.toLowerCase().includes('chatbot service is available');
      }),
      catchError((error) => {
        // For errors, log and return false
        console.warn(
          'Error checking chatbot availability:',
          error.status,
          error.message,
        );
        return [false];
      }),
    );
  }

  sendMessage(
    message: string,
    conversationId?: string,
    language?: string,
  ): Observable<ChatApiResponse> {
    const payload = {
      message,
      conversationId,
      language,
    };

    return this.http
      .post<ChatApiResponse>(
        `${this.chatbotApiUrl}/sendmessage`,
        payload,
        httpOptions,
      )
      .pipe(
        // eslint-disable-next-line rxjs-x/no-unsafe-takeuntil
        takeUntil(this.cancelRequest$),
        catchError((error) => {
          console.error('Chat API Error:', error);
          // eslint-disable-next-line rxjs-x/throw-error
          return throwError(() => ({
            message: 'Failed to send message',
            error: error.message || 'Unknown error occurred',
          }));
        }),
      );
  }

  public cancelOngoingRequest(): void {
    this.cancelRequest$.next();
  }
}
